/**
 * Reverse nomenclature lookups — resolve the MEDIS UUIDs that appointments and
 * results carry into human-readable names, using the local IndexedDB cache.
 *
 * Mirrors the mobile app's per-entity services:
 *   - physician  : `azure_physicians.master_physician_id` (UUID) → `full_name`
 *   - location   : `azure_locations.location_id` (UUID)        → `name` / address
 *   - specialty  : `azure_specialties.specialty_id` (UUID)     → `specialty_name`
 *   - degree     : `azure_degrees.degree_id` (UUID)            → `degree_name`
 *   - physician main degree: `azure_physician_roles` (physician local id →
 *     degree_id) joined to `azure_degrees`, active, lowest `display_order`.
 */

import { db, type NomenclatureRow } from './db';
import { revealRows } from './crypto';
import { clearNomenclature, syncNomenclatureIfStale } from './sync';

export interface ResolvedLocation {
  name: string;
  address?: string;
  town?: string;
  county?: string;
}

export interface NomenclatureResolver {
  physicianName(uuid?: string | null): string | null;
  /** Main degree/title for a physician, looked up by the physician's UUID. */
  physicianDegree(uuid?: string | null): string | null;
  locationName(uuid?: string | null): string | null;
  location(uuid?: string | null): ResolvedLocation | null;
  specialtyName(uuid?: string | null): string | null;
  /** Resolve a standalone degree UUID (e.g. the `Degree` field on appointments). */
  degreeName(uuid?: string | null): string | null;
}

const isActive = (v: unknown): boolean => v !== 0 && v !== false && v !== '0';

interface ResolverData {
  physicians: Map<string, { localId: number; fullName: string }>;
  locations: Map<string, ResolvedLocation>;
  specialties: Map<string, string>;
  degrees: Map<string, string>;
  /** physician local id → main degree name. */
  physicianDegrees: Map<number, string>;
}

let cache: Promise<ResolverData> | null = null;
let healed = false;

export function invalidateResolverCache(): void {
  cache = null;
}

/** AES-GCM decryption failure (e.g. cached ciphertext vs a regenerated key). */
function isDecryptError(e: unknown): boolean {
  return e instanceof DOMException && e.name === 'OperationError';
}

/**
 * Build the resolver, self-healing a crypto-key/ciphertext mismatch. If the
 * cached encrypted rows can't be decrypted (the key in `meta` no longer matches
 * the `_enc` blobs — e.g. a key got regenerated under existing data), the whole
 * reveal throws and the resolver would be empty (no physician/location names).
 * Recover once by wiping the cache + key and re-syncing with a fresh, consistent
 * key, then rebuild.
 */
async function build(): Promise<ResolverData> {
  try {
    return await buildOnce();
  } catch (e) {
    if (!healed && isDecryptError(e)) {
      healed = true;
      await clearNomenclature();
      await syncNomenclatureIfStale(true);
      return await buildOnce();
    }
    throw e;
  }
}

async function buildOnce(): Promise<ResolverData> {
  const [physiciansRaw, locationsRaw, specialties, degrees, roles] = await Promise.all([
    db.rows('azure_physicians').toArray(),
    db.rows('azure_locations').toArray(),
    db.rows('azure_specialties').toArray(),
    db.rows('azure_degrees').toArray(),
    db.rows('azure_physician_roles').toArray(),
  ]);

  const [physicians, locations] = await Promise.all([revealRows(physiciansRaw), revealRows(locationsRaw)]);

  const physicianMap = new Map<string, { localId: number; fullName: string }>();
  for (const p of physicians) {
    const uuid = String(p.master_physician_id ?? '');
    if (uuid) physicianMap.set(uuid, { localId: p.id as number, fullName: String(p.full_name ?? '') });
  }

  const locationMap = new Map<string, ResolvedLocation>();
  for (const l of locations) {
    const uuid = String(l.location_id ?? '');
    if (!uuid) continue;
    locationMap.set(uuid, {
      name: String(l.name ?? ''),
      address: l.address != null ? String(l.address) : undefined,
      town: l.town != null ? String(l.town) : undefined,
      county: l.county != null ? String(l.county) : undefined,
    });
  }

  const specialtyMap = new Map<string, string>();
  for (const s of specialties) {
    const uuid = String(s.specialty_id ?? '');
    if (uuid) specialtyMap.set(uuid, String(s.specialty_name ?? ''));
  }

  // Degree UUID → name (+ keep order/active for the per-physician resolution).
  const degreeMap = new Map<string, string>();
  const degreeMeta = new Map<string, { name: string; order: number; active: boolean }>();
  for (const d of degrees) {
    const uuid = String(d.degree_id ?? '');
    if (!uuid) continue;
    const name = String(d.degree_name ?? '');
    degreeMap.set(uuid, name);
    degreeMeta.set(uuid, {
      name,
      order: typeof d.display_order === 'number' ? d.display_order : Number(d.display_order ?? 9999),
      active: isActive(d.is_active),
    });
  }

  // Physician local id → main degree (active, lowest display_order).
  const physicianDegrees = new Map<number, string>();
  const best = new Map<number, { name: string; order: number }>();
  for (const r of roles as NomenclatureRow[]) {
    const physicianId = r.physician_id as number;
    const meta = degreeMeta.get(String(r.degree_id ?? ''));
    if (physicianId == null || !meta || !meta.active) continue;
    const current = best.get(physicianId);
    if (!current || meta.order < current.order) best.set(physicianId, { name: meta.name, order: meta.order });
  }
  for (const [id, v] of best) physicianDegrees.set(id, v.name);

  return {
    physicians: physicianMap,
    locations: locationMap,
    specialties: specialtyMap,
    degrees: degreeMap,
    physicianDegrees,
  };
}

function getData(): Promise<ResolverData> {
  if (!cache) cache = build();
  return cache;
}

const clean = (v: string | null): string | null => (v && v.trim() ? v : null);

/** Build a synchronous resolver over the cached maps (load once via `loadResolver`). */
function makeResolver(data: ResolverData): NomenclatureResolver {
  return {
    physicianName: (uuid) => (uuid ? clean(data.physicians.get(uuid)?.fullName ?? null) : null),
    physicianDegree: (uuid) => {
      if (!uuid) return null;
      const localId = data.physicians.get(uuid)?.localId;
      return localId != null ? clean(data.physicianDegrees.get(localId) ?? null) : null;
    },
    locationName: (uuid) => (uuid ? clean(data.locations.get(uuid)?.name ?? null) : null),
    location: (uuid) => (uuid ? (data.locations.get(uuid) ?? null) : null),
    specialtyName: (uuid) => (uuid ? clean(data.specialties.get(uuid) ?? null) : null),
    degreeName: (uuid) => (uuid ? clean(data.degrees.get(uuid) ?? null) : null),
  };
}

export async function loadResolver(): Promise<NomenclatureResolver> {
  return makeResolver(await getData());
}
